import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAdminMode, useUser } from '../providers/UserProvider';
import { useReaderRoutes } from '../providers/ReaderRoutesProvider';
import { FeedSwitcher } from './FeedSwitcher';
import { ThemeToggle } from './ThemeToggle';
import { useTheme } from '../hooks/useTheme';
import { getReaderExports } from '../repositories/readerRepository';
import { isSearchFocusShortcut } from '../utils/keyboard';
import { isModalOpen } from './ModalOverlay';

/**
 * The reader's header: brand, hotkey hints, account links.
 *
 * One header for every page a reader can reach, so the admin shell and the
 * remaining non-feed pages draw the same chrome as the feed itself.
 */
export function ReaderNav({
  search,
  brandTitle,
  feedSwitcher = false,
  onSignOut,
  exports,
  storyHotkeys = false,
}: {
  search?: ReactNode;
  exports?: { rss: string; markdown: string };
  /** The current feed's name, shown after "TLDR:"; Home has none. */
  brandTitle?: string;
  /** Draws the feed switcher after the brand; the reader's feed views have it. */
  feedSwitcher?: boolean;
  /** Draws a sign-out control; the feed leaves signing out to the account page. */
  onSignOut?: () => void;
  /** Draws the j/k/o/x hints; only pages with a keyboard-driven story list have them. */
  storyHotkeys?: boolean;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const routes = useReaderRoutes();
  const [menuOpen, setMenuOpen] = useState(false);
  const menu = useRef<HTMLDivElement>(null);
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);
  // Pages without their own export URLs link the account/default Home exports.
  const [defaultExports, setDefaultExports] = useState<{ rss: string; markdown: string } | null>(null);
  useEffect(() => {
    if (!menuOpen || exports || defaultExports) return;
    let disposed = false;
    void getReaderExports().then(links => { if (!disposed) setDefaultExports(links); }).catch(() => undefined);
    return () => { disposed = true; };
  }, [menuOpen, exports, defaultExports]);
  const menuExports = exports || defaultExports;
  useEffect(() => {
    if (!menuOpen) return;
    const onPress = (event: PointerEvent) => { if (!menu.current?.contains(event.target as Node)) setMenuOpen(false); };
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('pointerdown', onPress);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('pointerdown', onPress); document.removeEventListener('keydown', onKey); };
  }, [menuOpen]);
  // `/` focuses the page's search box. Pages whose search bar is not a text
  // field (a feed's prompt bar) send the reader to the edit screen, as the
  // Feeds page's search link does.
  useEffect(() => {
    if (!feedSwitcher) return;
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      const typing = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
      if (!isSearchFocusShortcut(event, typing) || isModalOpen()) return;
      event.preventDefault();
      const field = document.querySelector<HTMLTextAreaElement>('.beta-search-bar textarea');
      if (field) field.focus();
      else navigate(routes.newSearch);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [feedSwitcher, navigate, routes.newSearch]);
  const { user, isAdmin, isLoggedIn, logout } = useUser();
  const { theme, toggleTheme } = useTheme();
  const { adminMode, toggleAdminMode } = useAdminMode();
  return (
    <header className="beta-reader-nav">
      <div className="beta-reader-nav-left">
        <Link className="beta-reader-brand" to={routes.home} aria-label="TLDR home" title="TLDR home">
          {brandTitle ? 'TLDR:' : 'TLDR'}
        </Link>
        {feedSwitcher && <FeedSwitcher name={brandTitle} />}
      </div>
      {search}
      <div className="beta-reader-nav-right">
        {(storyHotkeys || isAdmin) && <div className="reader-hotkey-hint">
          {storyHotkeys && <><span><kbd>j</kbd> down</span><span><kbd>k</kbd> up</span><span><kbd>o</kbd> expand</span><span><kbd>x</kbd> hide</span></>}
          {isAdmin && <button type="button" className="reader-hotkey-toggle" onClick={toggleAdminMode} aria-pressed={adminMode} title="Toggle admin mode (a)"><kbd>a</kbd> admin</button>}
        </div>}
        <span className="reader-desktop-theme"><ThemeToggle /></span>
        <div className="beta-theme-menu" ref={menu}>
          <button type="button" className="beta-mobile-menu" aria-label="Reader menu" aria-expanded={menuOpen} onClick={() => setMenuOpen(open => !open)}><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><path d="M3 6h18M3 12h18M3 18h18" /></svg></button>
          {menuOpen && <div className="reader-popover reader-nav-menu">
            {isLoggedIn ? <Link to="/digests">Account</Link> : <Link className="beta-signin" to="/signup" state={{ backgroundLocation: location, from: location }}>Sign in</Link>}
            {(location.search.includes('search=') || location.search.includes('run=')) && <Link to={routes.feeds}>Your feeds</Link>}
            <button type="button" onClick={toggleTheme}>{theme === 'dark' ? 'Light theme' : 'Dark theme'}</button>
            {menuExports && <><a href={menuExports.rss}>RSS feed</a><a href={menuExports.markdown}>Markdown (.md)</a></>}
            {isLoggedIn && <button type="button" onClick={onSignOut || logout}>Sign out</button>}
          </div>}
        </div>
        {isLoggedIn ? <Link className="beta-reader-account reader-desktop-account" to="/digests" aria-label="Account" title={user?.email || user?.username}>{(user?.email || user?.username || 'A')[0].toUpperCase()}</Link> :
          <Link className="beta-signin reader-desktop-account" to="/signup" state={{ backgroundLocation: location, from: location }}>Sign in</Link>}
        {isLoggedIn && onSignOut && (
          <button type="button" className="beta-reader-signout" onClick={onSignOut}>Sign out</button>
        )}
      </div>
    </header>
  );
}
